import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import {
  CheckCircle2,
  Circle,
  Zap,
  ExternalLink,
  Settings2,
  X,
  Loader2,
  Globe,
  Rss,
  Webhook,
  Share2,
  Copy,
  ChevronRight,
  AlertCircle,
  Link2,
} from "lucide-react";
import { listConnectors, saveConnector, deleteConnector } from "@/lib/companyApi";

const CATEGORY_LABELS = {
  organic: "Organic / SEO",
  job_board: "Job Boards",
  social: "Social",
  automation: "Automation",
};

const SETUP_TYPE_ICONS = {
  auto: CheckCircle2,
  xml_feed: Rss,
  oauth: Share2,
  webhook: Webhook,
  deeplink: Link2,
};

const SETUP_TYPE_LABELS = {
  auto: "Auto",
  xml_feed: "XML Feed",
  oauth: "OAuth",
  webhook: "Webhook",
  deeplink: "Deep Link",
};

const WISHIRE_BASE = window.location.origin.includes("localhost")
  ? "http://localhost:8000"
  : "https://wishire.in";

export default function Connectors() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configModal, setConfigModal] = useState(null); // connector being configured
  const [saving, setSaving] = useState(false);
  const [configValues, setConfigValues] = useState({});
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await listConnectors();
      setConnectors(data);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(connector) {
    setSaving(true);
    try {
      await saveConnector(connector.id, configValues);
      setConfigModal(null);
      setConfigValues({});
      await load();
    } catch (e) {
      toast.error("Failed to save: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect(connectorId) {
    if (!(await confirm({ title: "Please confirm", description: "Disconnect this connector?", danger: true }))) return;
    await deleteConnector(connectorId);
    await load();
  }

  function copyText(text, key) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  const grouped = CATEGORY_LABELS
    ? Object.entries(CATEGORY_LABELS).map(([cat, label]) => ({
        category: cat,
        label,
        items: connectors.filter((c) => c.category === cat),
      })).filter((g) => g.items.length > 0)
    : [];

  const activeCount = connectors.filter(
    (c) => c.status === "active" || c.status === "connected"
  ).length;

  return (
    <div className="container-x py-10 md:py-14">
      {/* Header */}
      <div className="mb-12">
        <span className="overline">Integrations</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          Job Board{" "}
          <span className="italic text-stone-500">Connectors</span>
        </h1>
        <p className="mt-3 text-stone-600 max-w-2xl">
          Publish once on Wishire — distribute everywhere automatically. Your jobs reach
          Google, Indeed, LinkedIn and more without leaving this dashboard.
        </p>

        {!loading && (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium">
            <CheckCircle2 size={15} />
            {activeCount} of {connectors.length} connectors active
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-stone-500">
          <Loader2 size={18} className="animate-spin" /> Loading connectors…
        </div>
      ) : (
        <div className="space-y-12">
          {grouped.map(({ category, label, items }) => (
            <section key={category}>
              <div className="flex items-center gap-3 mb-5">
                <span className="overline">{label}</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((c) => (
                  <ConnectorCard
                    key={c.id}
                    connector={c}
                    copied={copied}
                    onCopy={copyText}
                    onConfigure={() => {
                      setConfigValues(c.config || {});
                      setConfigModal(c);
                    }}
                    onDisconnect={() => handleDisconnect(c.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Config Modal */}
      {configModal && (
        <ConfigModal
          connector={configModal}
          values={configValues}
          onChange={setConfigValues}
          onSave={() => handleSave(configModal)}
          onClose={() => { setConfigModal(null); setConfigValues({}); }}
          saving={saving}
          copied={copied}
          onCopy={copyText}
        />
      )}
    </div>
  );
}

function ConnectorCard({ connector: c, onConfigure, onDisconnect, copied, onCopy }) {
  const isActive = c.status === "active" || c.status === "connected";
  const TypeIcon = SETUP_TYPE_ICONS[c.setup_type] || Globe;

  return (
    <div
      className={`rounded-3xl border p-6 bg-white transition-all ${
        isActive ? "border-emerald-200 shadow-[0_4px_24px_-8px_rgba(16,185,129,0.15)]" : "border-stone-200"
      }`}
    >
      {/* Top row */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 rounded-2xl border border-stone-100 bg-stone-50 flex items-center justify-center shrink-0 overflow-hidden">
          {c.logo ? (
            <img src={c.logo} alt={c.name} className="w-6 h-6 object-contain" />
          ) : (
            <Zap size={18} className="text-stone-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg leading-snug">{c.name}</h3>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-stone-500 mt-0.5 leading-snug">{c.description}</p>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs text-stone-500 bg-stone-100 px-2.5 py-1 rounded-full">
          <TypeIcon size={11} />
          {SETUP_TYPE_LABELS[c.setup_type]}
        </span>
        {c.monthly_reach && (
          <span className="text-xs text-stone-400 font-mono">{c.monthly_reach}</span>
        )}
      </div>

      {/* Auto connector — show feed URL */}
      {c.setup_type === "auto" && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle2 size={15} className="shrink-0" />
          Active automatically — no setup required
        </div>
      )}

      {/* XML Feed connector */}
      {c.setup_type === "xml_feed" && (
        <div className="space-y-3">
          {c.id === "indeed_feed" || c.id === "internshala" ? (
            <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3">
              <p className="text-xs text-stone-500 mb-1.5 font-mono uppercase tracking-widest">Your XML Feed URL</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-stone-700 truncate font-mono">
                  {window.location.origin.includes("localhost")
                    ? "http://localhost:8000/api/feed/indeed.xml"
                    : "https://wishire.in/api/feed/indeed.xml"}
                </code>
                <button
                  onClick={() => onCopy(
                    window.location.origin.includes("localhost")
                      ? "http://localhost:8000/api/feed/indeed.xml"
                      : "https://wishire.in/api/feed/indeed.xml",
                    `feed-${c.id}`
                  )}
                  className="shrink-0 px-2.5 py-1.5 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100 text-xs flex items-center gap-1 transition-colors"
                >
                  {copied === `feed-${c.id}` ? <CheckCircle2 size={11} /> : <Copy size={11} />}
                  {copied === `feed-${c.id}` ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-xs text-stone-500 mt-2">
                {c.id === "indeed_feed"
                  ? "Submit to: indeed.com/hire → Job Feeds → Add XML feed URL"
                  : "Submit to: internshala.com/employer → Job Feeds"}
              </p>
            </div>
          ) : null}
        </div>
      )}

      {/* Deep link connector */}
      {c.setup_type === "deeplink" && (
        <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-700">
          <AlertCircle size={14} className="inline mr-1.5 shrink-0" />
          Requires {c.name} recruiter account. Use "Distribute" on any job to get a pre-filled posting link.
        </div>
      )}

      {/* Actions */}
      {(c.setup_type === "webhook" || c.setup_type === "oauth") && (
        <div className="flex items-center gap-2 mt-1">
          {isActive ? (
            <>
              <button
                onClick={onConfigure}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm transition-colors"
              >
                <Settings2 size={14} /> Edit
              </button>
              <button
                onClick={onDisconnect}
                className="px-4 py-2.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm transition-colors"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={onConfigure}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 text-sm transition-colors"
            >
              Connect <ChevronRight size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "active")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-mono uppercase tracking-wide">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Active
      </span>
    );
  if (status === "connected")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-mono uppercase tracking-wide">
        <CheckCircle2 size={10} />
        Connected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-stone-500 text-[11px] font-mono uppercase tracking-wide">
      <Circle size={10} />
      Not set up
    </span>
  );
}

function ConfigModal({ connector: c, values, onChange, onSave, onClose, saving, copied, onCopy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-[#FDFBF7] rounded-3xl shadow-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl">{c.name}</h2>
            <p className="text-sm text-stone-500 mt-0.5">Configure connector</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full border border-stone-200 hover:bg-stone-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {c.setup_type === "webhook" && (
          <WebhookConfig values={values} onChange={onChange} copied={copied} onCopy={onCopy} />
        )}

        {c.setup_type === "oauth" && c.id === "linkedin" && (
          <LinkedInConfig values={values} onChange={onChange} />
        )}

        <div className="flex gap-3 mt-8">
          <button onClick={onClose} className="flex-1 px-4 py-3 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 text-sm transition-colors">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 px-4 py-3 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? "Saving…" : "Save & Connect"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WebhookConfig({ values, onChange, copied, onCopy }) {
  const samplePayload = JSON.stringify(
    {
      event: "job.published",
      job_id: "job_abc123",
      title: "Senior Frontend Engineer",
      company: "Acme Corp",
      location: "Bengaluru",
      remote: false,
      url: "https://wishire.in/jobs/job_abc123",
      apply_url: "https://wishire.in/jobs/job_abc123?src=webhook",
      published_at: "2026-06-12T09:00:00Z",
    },
    null,
    2
  );

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          Webhook URL <span className="text-red-500">*</span>
        </label>
        <input
          type="url"
          placeholder="https://hooks.zapier.com/hooks/catch/..."
          value={values.webhook_url || ""}
          onChange={(e) => onChange({ ...values, webhook_url: e.target.value })}
          className="w-full px-4 py-2.5 rounded-2xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
        />
        <p className="text-xs text-stone-500 mt-1.5">
          Paste your Zapier, Make, or custom webhook URL. We'll POST JSON to this URL whenever a job is distributed.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs font-mono uppercase tracking-widest text-stone-500">Sample Payload</p>
          <button
            onClick={() => onCopy(samplePayload, "webhook-payload")}
            className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1 transition-colors"
          >
            {copied === "webhook-payload" ? <CheckCircle2 size={11} /> : <Copy size={11} />}
            {copied === "webhook-payload" ? "Copied" : "Copy"}
          </button>
        </div>
        <pre className="bg-stone-900 text-stone-100 rounded-2xl p-4 text-[11px] font-mono overflow-x-auto leading-relaxed">
          {samplePayload}
        </pre>
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-700 space-y-1">
        <p className="font-medium">Zapier setup in 3 steps:</p>
        <p>1. Create a new Zap → Trigger: Webhooks by Zapier → Catch Hook</p>
        <p>2. Copy the webhook URL above, paste it here, save</p>
        <p>3. Click "Distribute" on any job — Zapier catches it and routes to LinkedIn, Naukri etc.</p>
      </div>
    </div>
  );
}

function LinkedInConfig({ values, onChange }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-700">
        <p className="font-medium mb-1">LinkedIn auto-posting via OAuth</p>
        <p className="text-xs text-blue-600">
          In production, clicking "Connect" will redirect you to LinkedIn to authorize Wishire.
          Once authorized, new jobs will auto-post to your LinkedIn feed with a tracked apply link.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          LinkedIn Company Page URL (optional)
        </label>
        <input
          type="url"
          placeholder="https://www.linkedin.com/company/your-company"
          value={values.company_page_url || ""}
          onChange={(e) => onChange({ ...values, company_page_url: e.target.value })}
          className="w-full px-4 py-2.5 rounded-2xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          Post template
        </label>
        <textarea
          rows={3}
          placeholder="We're hiring {title} at {company}! Apply via Wishire 👇 {url}"
          value={values.post_template || ""}
          onChange={(e) => onChange({ ...values, post_template: e.target.value })}
          className="w-full px-4 py-2.5 rounded-2xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900 resize-none"
        />
        <p className="text-xs text-stone-500 mt-1">
          Variables: <code className="font-mono bg-stone-100 px-1 rounded">{"{title}"}</code>{" "}
          <code className="font-mono bg-stone-100 px-1 rounded">{"{company}"}</code>{" "}
          <code className="font-mono bg-stone-100 px-1 rounded">{"{url}"}</code>
        </p>
      </div>
    </div>
  );
}
